/**
 * SuperballBingoStyle1 sections — Classic Bingo Hall / Retro Aesthetic.
 * Warm amber/gold theme with bingo hall vibes.
 */
import React from 'react'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import BingoBall from '../../../components/BingoBall'
import { useContent } from '../../../contexts/ContentContext'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'
import { formatCurrencyValue } from '../../../utils/formatCurrency'

/**
 * Hero section — warm amber/gold retro theme, charity logo, animated floating balls.
 */
export function BingoStyle1HeroSection({
  partner,
  mainDraw,
  editMode,
  handleTranslationUpdate,
  purchaseNavigate, language,
}) {
  const { c } = useContent()
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot || 5750)
  const sessionSales = mainDraw?.session_sales || 480
  const sessionPrize = Math.round(sessionSales * 0.25)
  const ticketsSold = mainDraw?.total_tickets_sold

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const bingoCharityLogo = '/assets/bingo-charity-logo.svg'
  const logoUrl = isDefaultLogo ? bingoCharityLogo : branding?.logoUrl || bingoCharityLogo
  const logoAlt = isDefaultLogo ? 'Charity Partner' : branding?.charityName || brandName || 'Charity Partner'

  return (
    <>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-300/30 dark:bg-amber-500/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-yellow-300/25 dark:bg-yellow-500/15 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute top-1/2 right-1/3 w-64 h-64 bg-orange-200/30 dark:bg-amber-400/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '2s' }}
        />

        {/* Floating balls background */}
        <div
          className="absolute top-20 left-[10%] opacity-30 dark:opacity-20 animate-bounce"
          style={{ animationDuration: '3s' }}
        >
          <BingoBall number={7} size="lg" />
        </div>
        <div
          className="absolute top-32 right-[15%] opacity-25 dark:opacity-15 animate-bounce"
          style={{ animationDuration: '4s', animationDelay: '0.5s' }}
        >
          <BingoBall number={23} size="md" />
        </div>
        <div
          className="absolute bottom-32 left-[20%] opacity-30 dark:opacity-20 animate-bounce"
          style={{ animationDuration: '3.5s', animationDelay: '1s' }}
        >
          <BingoBall number={45} size="lg" />
        </div>
        <div
          className="absolute bottom-20 right-[25%] opacity-25 dark:opacity-15 animate-bounce"
          style={{ animationDuration: '4.5s', animationDelay: '1.5s' }}
        >
          <BingoBall number={68} size="md" />
        </div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <HeroSiteLogo tone="light" />
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="text-center">
            {logoUrl && (
              <div className="mb-8 flex justify-center">
                <div className="relative group">
                  <div className="absolute -inset-5 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
                  <div className="relative bg-white rounded-2xl px-10 py-6 shadow-2xl text-center">
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-3">
                      <InlineTranslationEdit
                        translationKey="superballBingo.fundraiserFor"
                        value={c('superballBingo.fundraiserFor', 'A Fundraiser For')}
                        fallback="A Fundraiser For"
                        onUpdate={handleTranslationUpdate}
                        editMode={editMode}
                      />
                    </p>
                    <img
                      src={logoUrl}
                      alt={logoAlt}
                      className="h-28 sm:h-36 lg:h-40 w-auto object-contain max-w-[360px] mx-auto"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Live Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-500/20 backdrop-blur-sm border border-emerald-300 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
              {mainDraw?.name || 'Session 42'} —{' '}
              <InlineTranslationEdit
                translationKey="superballBingo.liveNow"
                value={c('superballBingo.liveNow', 'LIVE NOW')}
                fallback="LIVE NOW"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 dark:text-white mb-4 leading-tight">
              <InlineTranslationEdit
                translationKey="superballBingo.superballLabel"
                value={c('superballBingo.superballLabel', 'SUPERBALL')}
                fallback="SUPERBALL"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 dark:from-amber-400 dark:via-yellow-300 dark:to-amber-400">
                <InlineTranslationEdit
                  translationKey="superballBingo.bingoLabel"
                  value={c('superballBingo.bingoLabel', 'BINGO')}
                  fallback="BINGO"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
            </h1>

            {/* Tagline */}
            <p className="text-xl md:text-2xl text-amber-700 dark:text-amber-200/80 mb-8 max-w-xl mx-auto">
              <InlineTranslationEdit
                translationKey="superballBingo.subtitle"
                value={c(
                  'superballBingo.subtitle',
                  "One ball hides the ★ Superball. Pick it and win the progressive jackpot!"
                )}
                fallback="One ball hides the ★ Superball. Pick it and win the progressive jackpot!"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
                multiline
              />
            </p>

            <HeroPartnerGlassSelect className="mx-auto mb-8 w-full max-w-md" />

            {/* Prize Display - Mobile */}
            <div className="lg:hidden mb-8">
              <div className="bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-500 rounded-3xl p-6 shadow-2xl shadow-amber-500/30">
                <p className="text-amber-900/80 font-bold uppercase text-sm tracking-wider mb-2">
                  <InlineTranslationEdit
                    translationKey="superballBingo.jackpotLabel"
                    value={c('superballBingo.jackpotLabel', 'Progressive Jackpot')}
                    fallback="Progressive Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <p className="text-5xl md:text-6xl font-black text-amber-900">
                  {formatCurrencyValue(jackpotAmount, { language })}
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => purchaseNavigate('/superball-bingo-purchase')}
              className="group relative w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-amber-900 text-xl font-black rounded-2xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/40"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <Ticket className="w-7 h-7" />
                <InlineTranslationEdit
                  translationKey="superballBingo.buyTicketsButton"
                  value={c('superballBingo.buyTicketsButton', 'GET YOUR TICKETS')}
                  fallback="GET YOUR TICKETS"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-amber-300 to-yellow-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>

            {/* Pricing */}
            <p className="text-amber-600 dark:text-amber-300/60 mt-4 text-lg">
              <InlineTranslationEdit
                translationKey="superballBingo.ticketPricing"
                value={c('superballBingo.ticketPricing', '$1 each or 6 for $5 • Valid this session only')}
                fallback="$1 each or 6 for $5 • Valid this session only"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>

            {/* Quick Stats Row */}
            <div className="flex flex-wrap justify-center gap-6 mt-8 text-amber-700 dark:text-amber-200/80">
              {ticketsSold != null && (
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <span>
                    <strong className="text-gray-900 dark:text-white">{ticketsSold}</strong>{' '}
                    <InlineTranslationEdit
                      translationKey="superballBingo.ticketsSoldLabel"
                      value={c('superballBingo.ticketsSoldLabel', 'tickets sold')}
                      fallback="tickets sold"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                <span>
                  <InlineTranslationEdit
                    translationKey="superballBingo.sessionPrizeLabel"
                    value={c('superballBingo.sessionPrizeLabel', 'Session prize:')}
                    fallback="Session prize:"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />{' '}
                  <strong className="text-gray-900 dark:text-white">${sessionPrize}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Jackpot & Countdown */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 rounded-[2rem] blur-xl opacity-30 animate-pulse" />

              <div className="relative bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-500 rounded-3xl p-8 shadow-2xl">
                <div className="absolute -top-8 -right-8">
                  <BingoBall number={76} size="xl" animated />
                </div>

                <div className="text-center">
                  <p className="text-amber-900/70 font-bold uppercase text-sm tracking-wider mb-2">
                    ★{' '}
                    <InlineTranslationEdit
                      translationKey="superballBingo.jackpotLabel"
                      value={c('superballBingo.jackpotLabel', 'Progressive Jackpot')}
                      fallback="Progressive Jackpot"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />{' '}
                    ★
                  </p>
                  <p className="text-6xl md:text-7xl font-black text-amber-900 mb-4">
                    {formatCurrencyValue(jackpotAmount, { language })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * How-it-works section — bingo instructions.
 */
export function BingoStyle1HowItWorksSection({ editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const steps = [
    { title: 'Buy Tickets', desc: 'Purchase your bingo tickets for the session', titleKey: 'SBStyle1.step1Title', descKey: 'SBStyle1.step1Desc' },
    { title: 'Pick Your Ball', desc: 'Choose your lucky ball or let us pick for you', titleKey: 'SBStyle1.step2Title', descKey: 'SBStyle1.step2Desc' },
    { title: 'Watch the Draw', desc: 'Watch the live ball draw in real-time', titleKey: 'SBStyle1.step3Title', descKey: 'SBStyle1.step3Desc' },
    { title: 'Win Big', desc: 'Match the superball to win the jackpot!', titleKey: 'SBStyle1.step4Title', descKey: 'SBStyle1.step4Desc' },
  ]

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
        <InlineTranslationEdit
          translationKey="superballBingo.howItWorksTitle"
          value={c('superballBingo.howItWorksTitle', 'How to Play')}
          fallback="How to Play"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {steps.map((step, i) => (
          <div key={i} className="text-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-black text-amber-500 mb-2">{i + 1}</div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              <InlineTranslationEdit translationKey={step.titleKey} value={c(step.titleKey, step.title)} fallback={step.title} onUpdate={handleTranslationUpdate} editMode={editMode} />
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <InlineTranslationEdit translationKey={step.descKey} value={c(step.descKey, step.desc)} fallback={step.desc} onUpdate={handleTranslationUpdate} editMode={editMode} />
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Final CTA section.
 */
export function BingoStyle1FinalCTASection({ editMode, handleTranslationUpdate, purchaseNavigate }) {
  const { c } = useContent()

  return (
    <div className="max-w-3xl mx-auto px-4 text-center">
      <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
        <InlineTranslationEdit
          translationKey="SBStyle1.readyToPlay"
          value={c('SBStyle1.readyToPlay', 'Ready to Play?')}
          fallback="Ready to Play?"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
        <InlineTranslationEdit
          translationKey="SBStyle1.finalCTADesc"
          value={c('SBStyle1.finalCTADesc', 'Get your tickets now and try your luck at the Superball jackpot!')}
          fallback="Get your tickets now and try your luck at the Superball jackpot!"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </p>
      <button
        onClick={() => purchaseNavigate('/superball-bingo-purchase')}
        className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-amber-900 text-xl font-black rounded-2xl hover:scale-105 transition-all"
      >
        <Ticket className="w-6 h-6" />
        <InlineTranslationEdit
          translationKey="superballBingo.buyTicketsButton"
          value={c('superballBingo.buyTicketsButton', 'Buy Tickets Now')}
          fallback="Buy Tickets Now"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
        <ArrowRight className="w-6 h-6" />
      </button>
    </div>
  )
}
